/**
 * Etapa 2: Validação de contatos do CSV
 * Verifica formato de telefone, duplicatas e existência no sistema
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Users,
  UserPlus,
  UserCheck,
  XCircle,
  ChevronRight,
  Loader2,
  Copy,
  Link,
  Shield
} from 'lucide-react';

import { CSVParseResult, RowValidation } from '@/lib/csv-utils';
import { ValidationStats, ImportProgress } from '@/hooks/useListImport';

// =====================================================
// TIPOS
// =====================================================

interface ContactValidationStepProps {
  csvData: CSVParseResult | null;
  validatedRows: RowValidation[];
  validationStats: ValidationStats | null;
  isValidating: boolean;
  progress: ImportProgress | null;
  updateExisting: boolean;
  onUpdateExistingChange: (value: boolean) => void;
  /** Permitir linhas duplicadas (mesmo contato, dados diferentes - ex: múltiplos veículos) */
  allowDuplicates: boolean;
  onAllowDuplicatesChange: (value: boolean) => void;
  /** Importar somente registros válidos, ignorando linhas com erro */
  importOnlyValid: boolean;
  onImportOnlyValidChange: (value: boolean) => void;
  onUpdateRow: (rowIndex: number, field: string, value: string) => void;
  onRevalidate: () => void;
  onComplete: () => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function ContactValidationStep({
  csvData,
  validatedRows,
  validationStats,
  isValidating,
  progress,
  updateExisting,
  onUpdateExistingChange,
  allowDuplicates,
  onAllowDuplicatesChange,
  importOnlyValid,
  onImportOnlyValidChange,
  onUpdateRow,
  onRevalidate,
  onComplete
}: ContactValidationStepProps) {
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);
  
  // Verificar se pode avançar - duplicatas não bloqueiam se allowDuplicates está ativo, blacklist sempre bloqueia
  const hasInvalidRows = validatedRows.some(v => !v.isValid);
  const hasDuplicates = validatedRows.some(v => v.isDuplicate);
  const hasBlacklisted = validatedRows.some(v => v.isBlacklisted);
  const hasBlockingErrors = hasInvalidRows || (hasDuplicates && !allowDuplicates);

  // Quantidade de registros que realmente serão gravados
  // (mesmo filtro aplicado por importList em useListImport)
  const importableCount = validatedRows.filter(v =>
    v.isValid && !v.isBlacklisted && (allowDuplicates || !v.isDuplicate)
  ).length;
  const ignoredCount = validatedRows.length - importableCount;

  // Com "importar somente válidos" ativo, erros deixam de bloquear o avanço
  const canContinue = validatedRows.length > 0 && !isValidating && (
    importOnlyValid ? importableCount > 0 : !hasBlockingErrors
  );
  
  // Filtrar linhas para exibição
  const displayRows = showOnlyErrors 
    ? validatedRows.filter(v => !v.isValid || (v.isDuplicate && !allowDuplicates))
    : validatedRows;
  
  // =====================================================
  // RENDER
  // =====================================================
  
  if (isValidating && progress) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center space-y-2">
          <p className="font-medium">{progress.message}</p>
          <p className="text-sm text-muted-foreground">
            {progress.current} de {progress.total}
          </p>
        </div>
        <Progress value={(progress.current / progress.total) * 100} className="w-64" />
      </div>
    );
  }
  
  return (
    <div 
      className="space-y-6 w-full" 
      style={{ 
        maxWidth: '100%', 
        overflow: 'hidden', 
        contain: 'inline-size' 
      }}
    >
      {/* Cards de estatísticas */}
      {validationStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{validationStats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-green-600">{validationStats.newContacts}</p>
                  <p className="text-xs text-muted-foreground">Novos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-blue-600">{validationStats.existingInSystem}</p>
                  <p className="text-xs text-muted-foreground">Existentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-2xl font-bold text-destructive">{validationStats.invalid}</p>
                  <p className="text-xs text-muted-foreground">Com erros</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Card de Blacklist LGPD */}
          {validationStats.blacklisted > 0 && (
            <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{validationStats.blacklisted}</p>
                    <p className="text-xs text-muted-foreground">Blacklist LGPD</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Opções */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="updateExisting"
              checked={updateExisting}
              onCheckedChange={(checked) => onUpdateExistingChange(checked === true)}
            />
            <label htmlFor="updateExisting" className="text-sm">
              Atualizar nome/email de contatos existentes
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="allowDuplicates"
              checked={allowDuplicates}
              onCheckedChange={(checked) => {
                onAllowDuplicatesChange(checked === true);
                // Revalidar após mudar opção
                onRevalidate();
              }}
            />
            <label htmlFor="allowDuplicates" className="text-sm">
              Permitir linhas duplicadas (mesmo contato, dados diferentes)
            </label>
          </div>
          
          {/* Permite prosseguir importando apenas as linhas válidas */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="importOnlyValid"
              checked={importOnlyValid}
              onCheckedChange={(checked) => onImportOnlyValidChange(checked === true)}
            />
            <label htmlFor="importOnlyValid" className="text-sm">
              Importar somente os registros válidos (ignorar linhas com erro)
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showErrors"
              checked={showOnlyErrors}
              onCheckedChange={(checked) => setShowOnlyErrors(checked === true)}
            />
            <label htmlFor="showErrors" className="text-sm">
              Mostrar apenas erros
            </label>
          </div>
        </div>
        
        <Button variant="outline" size="sm" onClick={onRevalidate}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Revalidar
        </Button>
      </div>
      
      {/* Alerta informativo quando o usuário optou por importar somente os válidos */}
      {importOnlyValid && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Serão importados <strong>{importableCount}</strong> registros válidos.{' '}
            {ignoredCount > 0 && (
              <>
                <strong>{ignoredCount}</strong> linhas com erro/duplicadas serão ignoradas e não farão parte da lista.
                Você pode corrigi-las e criar uma nova importação depois.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Alerta de linhas inválidas */}
      {hasInvalidRows && !importOnlyValid && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Existem contatos com erros de formato. Corrija os problemas abaixo para continuar
            ou marque "Importar somente os registros válidos".
          </AlertDescription>
        </Alert>
      )}
      
      {/* Alerta de duplicatas */}
      {validationStats && validationStats.duplicatesInCSV > 0 && (
        <Alert variant={allowDuplicates ? "default" : "destructive"}>
          {allowDuplicates ? <Link className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <AlertDescription>
            {allowDuplicates ? (
              <>
                <strong>{validationStats.duplicatesInCSV}</strong> linhas duplicadas serão vinculadas 
                ao mesmo contato. Cada linha criará um item separado na lista (útil para 
                múltiplos veículos, boletos, etc.).
              </>
            ) : (
              <>
                Existem <strong>{validationStats.duplicatesInCSV}</strong> telefones duplicados. 
                Marque "Permitir linhas duplicadas" se cada linha representa uma entidade 
                diferente (veículo, boleto, etc.).
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* Alerta de blacklist LGPD */}
      {validationStats && validationStats.blacklisted > 0 && (
        <Alert className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <Shield className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            <strong>{validationStats.blacklisted}</strong> {validationStats.blacklisted === 1 ? 'contato está' : 'contatos estão'} na 
            blacklist LGPD e <strong>não serão importados</strong>. Esses contatos solicitaram não receber campanhas.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Alerta de contatos existentes */}
      {validationStats && validationStats.existingInSystem > 0 && !updateExisting && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {validationStats.existingInSystem} contatos já existem no sistema e serão ignorados.
            Marque "Atualizar nome/email" para atualizar seus dados.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Tabela de validação - com containment robusto */}
      <div 
        className="relative" 
        style={{ 
          width: '100%', 
          maxWidth: '100%', 
          overflow: 'hidden' 
        }}
      >
        {/* Gradiente de fade à direita indicando mais conteúdo */}
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
        
        <div 
          className="border rounded-md overflow-auto scrollbar-thin"
          style={{ 
            width: '100%',
            maxWidth: '100%',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 whitespace-nowrap">#</TableHead>
                  <TableHead className="w-24 whitespace-nowrap">Status</TableHead>
                  <TableHead className="whitespace-nowrap">Telefone</TableHead>
                  <TableHead className="whitespace-nowrap">Nome</TableHead>
                  <TableHead className="whitespace-nowrap">Erros</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
            {displayRows.map((validation) => {
                // Somente aplica estilo de erro se: inválido OU duplicata NÃO permitida
                // IMPORTANTE: Blacklist NÃO é erro bloqueante - apenas aviso visual
                const hasErrorStyle = !validation.isValid || (validation.isDuplicate && !allowDuplicates);
                const hasBlacklistStyle = validation.isBlacklisted && !hasErrorStyle;
                return (
                <TableRow 
                  key={validation.rowIndex}
                  className={hasErrorStyle ? 'bg-destructive/5' : hasBlacklistStyle ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}
                >
                  <TableCell className="text-muted-foreground">
                    {validation.rowIndex + 2}
                  </TableCell>
                  <TableCell>
                    {validation.isBlacklisted ? (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                        <Shield className="h-3 w-3 mr-1" />
                        Blacklist
                      </Badge>
                    ) : validation.isDuplicate ? (
                      allowDuplicates ? (
                        <Badge variant="outline" className="text-blue-600 border-blue-300">
                          <Link className="h-3 w-3 mr-1" />
                          Vinculado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          Duplicado
                        </Badge>
                      )
                    ) : !validation.isValid ? (
                      <Badge variant="destructive">Erro</Badge>
                    ) : validation.existsInSystem ? (
                      <Badge variant="secondary">Existe</Badge>
                    ) : (
                      <Badge variant="default">Novo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.row === validation.rowIndex && editingCell?.field === 'telefone' ? (
                      <Input
                        value={validation.row.telefone}
                        onChange={(e) => onUpdateRow(validation.rowIndex, 'telefone', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                        autoFocus
                        className="h-8"
                      />
                    ) : (
                      <span 
                        className={`cursor-pointer hover:underline ${!validation.isValid ? 'text-destructive' : ''}`}
                        onClick={() => setEditingCell({ row: validation.rowIndex, field: 'telefone' })}
                      >
                        {validation.row.telefone}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingCell?.row === validation.rowIndex && editingCell?.field === 'nome' ? (
                      <Input
                        value={validation.row.nome || ''}
                        onChange={(e) => onUpdateRow(validation.rowIndex, 'nome', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingCell(null)}
                        autoFocus
                        className="h-8"
                      />
                    ) : (
                      <span 
                        className="cursor-pointer hover:underline"
                        onClick={() => setEditingCell({ row: validation.rowIndex, field: 'nome' })}
                      >
                        {validation.row.nome || '-'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {validation.errors.length > 0 && (
                      <span className="text-sm text-destructive">
                        {validation.errors.join('; ')}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
              })}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </div>
      
      {/* Botão de continuar */}
      <div className="flex justify-end">
        <Button onClick={onComplete} disabled={!canContinue}>
          {importOnlyValid ? (
            importableCount > 0 ? (
              <>
                Continuar com {importableCount} válidos
                <ChevronRight className="h-4 w-4 ml-2" />
              </>
            ) : (
              'Nenhum registro válido para importar'
            )
          ) : hasBlockingErrors ? (
            'Corrija os erros para continuar'
          ) : (
            <>
              Continuar
              <ChevronRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
